import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Notification } from "./useNotifications";

import { ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { ToastType } from "@/enums";

interface DismissNotificationRequest {
  workspaceId: number;
  notificationId: number;
}

interface DismissNotificationResponse {
  success: boolean;
  message: string;
  notification: Notification;
}

export const useDismissNotification = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation<DismissNotificationResponse, string, DismissNotificationRequest>({
    mutationFn: async ({ workspaceId, notificationId }: DismissNotificationRequest) => {
      const response = await fetch(ENDPOINTS.dismiss_notification(workspaceId, notificationId), {
        method: "DELETE",
        headers: jsonAuthHeaders(userToken),
      });

      return handleResponse(response);
    },
    onSuccess: (data, { workspaceId }) => {
      displayToast(data.message || "Notification dismissed", ToastType.success);
      queryClient.invalidateQueries({ queryKey: ["notifications", String(workspaceId)] });
    },
    onError: (error: string) => {
      displayErrorToast(error || "Failed to dismiss notification");
    },
  });
};

export default useDismissNotification;
